import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ApiFilterToggles,
  DayRange,
  InternalOpsPage,
  InternalOpsState,
  PageControls,
  TABLE_PAGE_SIZE,
  rowsForPage,
  UsageChart,
  UsageLimitCharts,
  formatCount,
  formatWhen,
  SortableTable,
  useApiFilters,
  useDayRange,
  useInternalOpsFetch,
  usageQuery,
  withQuery,
} from "./internalOpsUi";
import { API_ENDPOINTS } from "../../config/apiConfig";

const FIELD_GROUPS = [
  ["account", "Account"],
  ["payment", "Payment"],
  ["other", "Other"],
];

const CHART_FIELDS = new Set([
  "total_route_usage",
  "route_usage_limit",
  "optimization_runs_this_month",
  "optimization_usage_limit",
  "optimization_usage_reset_date",
  "total_optimization_runs",
  "last_optimization_run",
  "total_depot_usage",
  "depot_usage_limit",
  "total_truck_usage",
]);

function formatField(value) {
  if (value == null || value === "") return "—";
  if (value === true) return "Yes";
  if (value === false) return "No";
  return String(value);
}

export default function InternalOpsUsers() {
  const [query, setQuery] = useState("");
  const [days, setDays] = useDayRange();
  const filters = useApiFilters();
  const filterKey = `${query}|${days}|${filters.excludeStaff}|${filters.excludeAnonymous}`;
  const [paging, setPaging] = useState({ filterKey, page: 1 });
  if (paging.filterKey !== filterKey) {
    setPaging({ filterKey, page: 1 });
  }
  const page = paging.filterKey === filterKey ? paging.page : 1;
  const setPage = (next) => setPaging({ filterKey, page: next });
  const url = useMemo(
    () => withQuery(API_ENDPOINTS.internalOpsUsers, usageQuery(days, filters, { q: query, page, page_size: TABLE_PAGE_SIZE })),
    [query, days, filters, page]
  );
  const { data, loading, error } = useInternalOpsFetch(url);

  return (
    <InternalOpsPage title="Users" subtitle="Accounts, plans, and the APIs each person has called.">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search email, username, company"
          className="w-full max-w-md rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
        <div className="flex flex-wrap items-center gap-4">
          <ApiFilterToggles
            excludeStaff={filters.excludeStaff}
            excludeAnonymous={filters.excludeAnonymous}
            onExcludeStaff={filters.setExcludeStaff}
            onExcludeAnonymous={filters.setExcludeAnonymous}
          />
          <DayRange days={days} onChange={setDays} />
        </div>
      </div>
      <InternalOpsState loading={loading} error={error}>
        {data ? (
          <div className="min-w-0 space-y-3">
            <p className="text-sm text-slate-500">{data.count} matching accounts</p>
            <PageControls
              page={data.page || page}
              pageCount={data.page_count}
              count={data.count}
              pageSize={data.page_size || TABLE_PAGE_SIZE}
              onPage={setPage}
            />
            <SortableTable
              paginate={false}
              rows={rowsForPage(data.results, data.page || page, data.page_size || TABLE_PAGE_SIZE)}
              empty="No accounts match this search."
              rowKey={(row) => row.id}
              columns={[
                {
                  key: "email",
                  label: "Email",
                  sortValue: (row) => row.email,
                  render: (row) => (
                    <Link
                      to={withQuery(`/internal/users/${row.id}`, usageQuery(days, filters))}
                      className="font-medium text-blue-700 hover:underline"
                    >
                      {row.email}
                    </Link>
                  ),
                },
                { key: "company_name", label: "Company", sortValue: (row) => row.company_name || "", render: (row) => row.company_name || "—" },
                { key: "subscription_type", label: "Plan", sortValue: (row) => row.subscription_type || "", render: (row) => row.subscription_type || "—" },
                { key: "subscription_status", label: "Status", sortValue: (row) => row.subscription_status || "", render: (row) => row.subscription_status || "—" },
                { key: "email_verified", label: "Verified", sortValue: (row) => Boolean(row.email_verified), render: (row) => (row.email_verified ? "Yes" : "No") },
                { key: "api_hits", label: `API hits (${data.days}d)`, sortValue: (row) => row.api_hits, render: (row) => formatCount(row.api_hits) },
                {
                  key: "date_joined",
                  label: "Joined",
                  cellClassName: "text-slate-500",
                  sortValue: (row) => row.date_joined,
                  render: (row) => (row.date_joined ? new Date(row.date_joined).toLocaleDateString() : "—"),
                },
              ]}
            />
            {(data.allowlist || []).length ? (
              <p className="text-xs text-slate-500">
                Production allowlist: {data.allowlist.map((entry) => entry.email).join(", ")}
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                Production also requires Django staff or an Internal tool allowlist entry in admin.
              </p>
            )}
          </div>
        ) : null}
      </InternalOpsState>
    </InternalOpsPage>
  );
}

export function InternalOpsUserDetail() {
  const { userId } = useParams();
  const [days, setDays] = useDayRange();
  const filters = useApiFilters();
  const { data, loading, error } = useInternalOpsFetch(
    withQuery(API_ENDPOINTS.internalOpsUser(userId), usageQuery(days, filters))
  );
  const user = data?.user;
  const api = data?.api;
  const fields = data?.fields || [];

  return (
    <InternalOpsPage
      title={user?.username || user?.email || "User"}
      subtitle={user ? user.email : "API calls from this account."}
    >
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Link to={withQuery("/internal/users", usageQuery(days, filters))} className="text-sm font-medium text-blue-700 hover:underline">
          All users
        </Link>
        <div className="flex flex-wrap items-center gap-4">
          <ApiFilterToggles
            excludeStaff={filters.excludeStaff}
            excludeAnonymous={filters.excludeAnonymous}
            onExcludeStaff={filters.setExcludeStaff}
            onExcludeAnonymous={filters.setExcludeAnonymous}
          />
          <DayRange days={days} onChange={setDays} />
        </div>
      </div>
      <InternalOpsState loading={loading} error={error}>
        {user && api ? (
          <div className="space-y-4">
            <UsageLimitCharts limits={data.limits} />
            {FIELD_GROUPS.map(([group, title]) => {
              const rows = fields.filter((field) => field.group === group && !CHART_FIELDS.has(field.name));
              if (!rows.length) return null;
              return (
                <section key={group} className="space-y-2">
                  <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
                  <SortableTable
                    rows={rows}
                    rowKey={(row) => row.name}
                    columns={[
                      {
                        key: "title",
                        label: "Field",
                        sortValue: (row) => row.title,
                        render: (row) => row.title,
                      },
                      {
                        key: "value",
                        label: "Value",
                        sortValue: (row) => row.value,
                        render: (row) => (
                          <span className={row.censored ? "text-slate-500" : "text-slate-900"}>
                            {formatField(row.value)}
                          </span>
                        ),
                      },
                    ]}
                  />
                </section>
              );
            })}
            <UsageChart
              rows={api.by_day}
              series={[
                { key: "hits", name: "Hits", stroke: "#0f172a", fill: "#e2e8f0" },
                { key: "error_hits", name: "Errors", stroke: "#dc2626", fill: "#fecaca" },
              ]}
            />
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-slate-900">Applications</h2>
              <SortableTable
                rows={api.apps || []}
                empty="This user has not called an API in this window."
                rowKey={(row) => row.app}
                columns={[
                  {
                    key: "title",
                    label: "Application",
                    sortValue: (row) => row.title,
                    render: (row) => (
                      <Link
                        to={withQuery(`/internal/api-usage/${encodeURIComponent(row.app)}`, usageQuery(days, filters))}
                        className="font-medium text-blue-700 hover:underline"
                      >
                        {row.title}
                      </Link>
                    ),
                  },
                  { key: "hits", label: "Hits", sortValue: (row) => row.hits, render: (row) => formatCount(row.hits) },
                  {
                    key: "endpoint_count",
                    label: "Endpoints",
                    sortValue: (row) => row.endpoint_count,
                    render: (row) => formatCount(row.endpoint_count),
                  },
                ]}
              />
            </section>
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">Endpoints</h2>
              <SortableTable
                rows={api.endpoints}
                empty="No API calls in this window."
                rowKey={(row) => `${row.app}-${row.method}-${row.route}`}
                columns={[
                  {
                    key: "app_title",
                    label: "Application",
                    sortValue: (row) => row.app_title,
                    render: (row) => (
                      <Link
                        to={withQuery(`/internal/api-usage/${encodeURIComponent(row.app)}`, usageQuery(days, filters))}
                        className="font-medium text-blue-700 hover:underline"
                      >
                        {row.app_title}
                      </Link>
                    ),
                  },
                  {
                    key: "title",
                    label: "Endpoint",
                    sortValue: (row) => row.title,
                    render: (row) => (
                      <>
                        <Link
                          to={withQuery(`/internal/api-usage/${encodeURIComponent(row.app)}/endpoint`, usageQuery(days, filters, {
                            method: row.method,
                            route: row.route,
                          }))}
                          className="font-medium text-blue-700 hover:underline"
                        >
                          {row.title}
                        </Link>
                        <p className="mt-1 font-mono text-xs text-slate-500">
                          {row.method} {row.route}
                        </p>
                      </>
                    ),
                  },
                  { key: "hits", label: "Hits", sortValue: (row) => row.hits, render: (row) => formatCount(row.hits) },
                  {
                    key: "error_hits",
                    label: "Errors",
                    headerClassName: "text-red-700",
                    cellClassName: "text-red-700",
                    sortValue: (row) => row.error_hits,
                    render: (row) => formatCount(row.error_hits),
                  },
                  {
                    key: "last_seen",
                    label: "Last seen",
                    cellClassName: "text-slate-500",
                    sortValue: (row) => row.last_seen,
                    render: (row) => formatWhen(row.last_seen),
                  },
                ]}
              />
            </section>
          </div>
        ) : null}
      </InternalOpsState>
    </InternalOpsPage>
  );
}
